import React from "react";

import { Col, Row } from "antd";

import {
  formatLegalAuditDob,
  useLegalAuditDocumentRedaction,
} from "manager/hooks/useLegalAuditDocumentRedaction";
import { formatDate, getValueOrNA } from "shared/utils/ui";

import FlexContainer from "../FlexContainer";
import Icon from "../Icon";
import Tile from "../Tile";
import { RegularText } from "../Typography";

import { InfoListIcon } from "./styled";

export interface AamvaVerificationCardProps {
  name?: string;
  dateOfBirth?: string;
  documentId?: string;
  expirationDate?: string;
  issueDate?: string;
  postalCode?: string;
  issuingAuthority?: string;
  inset?: boolean;
}

/**
 * Card component, displaying AAMVA verification information.
 * Shows the data that was submitted to the AAMVA network for verification.
 */
const AamvaVerificationCard = ({
  name,
  dateOfBirth,
  documentId,
  expirationDate,
  issueDate,
  postalCode,
  issuingAuthority,
  inset = false,
}: AamvaVerificationCardProps) => {
  const { shouldRedactDocuments } = useLegalAuditDocumentRedaction();
  const attributes = [
    {
      label: "Name",
      value: getValueOrNA(name),
      icon: <Icon.PeopleIconXs />,
    },
    {
      label: "Date of Birth",
      value: shouldRedactDocuments
        ? formatLegalAuditDob(dateOfBirth)
        : formatDate(dateOfBirth),
      icon: <Icon.CalendarIconXs />,
    },
    {
      label: "Document ID",
      value: getValueOrNA(documentId),
      icon: <Icon.IdCardIconXs />,
    },
    {
      label: "Expiration Date",
      value: shouldRedactDocuments
        ? formatLegalAuditDob(expirationDate)
        : formatDate(expirationDate),
      icon: <Icon.FileFailedIconXs />,
    },
    {
      label: "Issue Date",
      value: formatDate(issueDate),
      icon: <Icon.CalendarIconXs />,
    },
    {
      label: "Postal Code",
      value: getValueOrNA(postalCode),
      icon: <Icon.LocationIconXs />,
    },
    {
      label: "Issuing Authority",
      value: getValueOrNA(issuingAuthority),
      icon: <Icon.IdCardIconXs />,
    },
  ];

  return (
    <Tile.Inner inset={inset}>
      <Row gutter={[16, 5]}>
        {attributes.map((attr) => (
          <Col key={attr.label} xs={24} sm={12} md={8} lg={6}>
            <Col>
              <Tile.Label>
                <FlexContainer alignItems="center">
                  <InfoListIcon>{attr.icon}</InfoListIcon>
                  <RegularText extraStrong>{attr.label}</RegularText>
                </FlexContainer>
              </Tile.Label>
              <RegularText>{attr.value}</RegularText>
            </Col>
          </Col>
        ))}
      </Row>
    </Tile.Inner>
  );
};

export default AamvaVerificationCard;
